// ── ObserverPicker (P12.2) ──
// Shown when SuperAdmin opens an observable surface without ?observeAs=.
// Picking redirects with the param so the identity hook adopts it and writes
// sessionStorage (never localStorage).

import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Eye } from "lucide-react";
import { Button } from "@/components/ui/button";

export interface PickerOption {
  id: string;
  label: string;
  sublabel?: string;
}

interface Props {
  title: string;
  options: PickerOption[];
  /** Path to redirect to with ?observeAs=ID appended (e.g. "/contractor-portal"). */
  basePath: string;
  onCancel?: () => void;
}

export function ObserverPicker({ title, options, basePath, onCancel }: Props) {
  const navigate = useNavigate();
  const [selected, setSelected] = useState<string>("");

  const confirm = () => {
    if (!selected) return;
    navigate(`${basePath}?observeAs=${encodeURIComponent(selected)}`, {
      replace: true,
    });
  };

  return (
    <div
      data-testid="observer-picker"
      className="min-h-screen flex items-center justify-center bg-background p-6"
    >
      <div className="max-w-sm w-full rounded-xl border border-border bg-card p-6">
        <div className="flex items-center gap-2 mb-3">
          <div className="h-9 w-9 rounded-lg bg-amber-500/15 text-amber-700 dark:text-amber-200 flex items-center justify-center">
            <Eye className="h-4 w-4" />
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground">
              SuperAdmin · Pilot View
            </p>
            <h1 className="text-sm font-semibold leading-tight">{title}</h1>
          </div>
        </div>
        <p className="text-xs text-muted-foreground mb-3">
          Read-only observation. No actions will be sent on the observed
          account's behalf.
        </p>
        <select
          data-testid="observer-picker-select"
          value={selected}
          onChange={(e) => setSelected(e.target.value)}
          className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm mb-3"
        >
          <option value="">— Choose —</option>
          {options.map((o) => (
            <option key={o.id} value={o.id}>
              {o.label}
              {o.sublabel ? ` (${o.sublabel})` : ""}
            </option>
          ))}
        </select>
        <div className="flex items-center gap-2">
          <Button
            data-testid="observer-picker-confirm"
            onClick={confirm}
            disabled={!selected}
            className="flex-1"
          >
            Observe
          </Button>
          {onCancel && (
            <Button variant="ghost" onClick={onCancel}>
              Cancel
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
